/**
 * Terms of Service body rendered inside the Settings FullScreenSheet.
 * Mirrors Figma node 8315:72075 (Yaruta Design System v1).
 */
export function TermsOfServiceBody() {
  return (
    <div className="flex flex-col gap-3 text-base leading-6 text-foreground">
      {/* 16px medium #5e5e5e — Figma 10611:95789 (was 14px, YAR-180). */}
      <p className="text-base font-medium leading-6 text-muted-foreground">
        Effective Date: September 2026
      </p>

      <p>
        By using Yaruta through yaruta.app, getyaruta.com, or related product
        experiences, you agree to these Terms.
      </p>

      <Section title="1. Service Overview">
        <p>
          Yaruta helps households organize family logistics by converting
          approved email communications into summaries, reminders, and shared
          schedules.
        </p>
      </Section>

      <Section title="2. User Responsibility">
        <p>You are responsible for:</p>
        <List>
          <li>
            Connecting only inboxes and accounts you are authorized to use
          </li>
          <li>Reviewing extracted tasks and events before committing them</li>
          <li>Managing who has access to your household workspace</li>
          <li>Ensuring collaborator permissions are appropriate</li>
        </List>
      </Section>

      <Section title="3. AI-Generated Suggestions">
        <p>
          Yaruta uses AI to suggest summaries, tasks, reminders, and calendar
          events based on connected email communications.
        </p>
        <p>
          These outputs are recommendations only and may not always be accurate.
        </p>
        <p>
          Parents remain responsible for reviewing all extracted information
          before relying on it.
        </p>
        <p className="font-medium">AI suggests, parents approve.</p>
      </Section>

      <Section title="4. Availability">
        <p>
          We may update, modify, or temporarily suspend parts of the service as
          the MVP evolves.
        </p>
      </Section>

      <Section title="5. Acceptable Use">
        <p>You may not:</p>
        <List>
          <li>Misuse connected inbox permissions</li>
          <li>Attempt unauthorized access</li>
          <li>Upload harmful code</li>
          <li>Use Yaruta for unlawful purposes</li>
        </List>
      </Section>

      <Section title="6. Limitation of Liability">
        <p>
          Yaruta is provided &ldquo;as is&rdquo; during MVP and beta phases.
        </p>
        <p>
          We are not liable for missed deadlines, school events, household
          scheduling conflicts, or decisions based on unreviewed AI-generated
          outputs.
        </p>
      </Section>

      {/* Twilio RCS sender review (YAR-461). Mirrored in pages/TermsAndConditions.tsx. */}
      <Section title="7. SMS/RCS Communications">
        <p>
          If you opt in to Yaruta text messaging, you agree to receive recurring
          service-related SMS and/or RCS messages from Yaruta about family
          schedules, events, tasks, reminders, daily digests, and items
          requiring review.
        </p>
        <p>Message frequency varies. Message and data rates may apply.</p>
        <p>
          Reply HELP for help or contact{' '}
          <a
            className="underline font-medium"
            href="mailto:hello@yaruta.app"
          >
            hello@yaruta.app
          </a>
          . Reply STOP to cancel and opt out of future recurring messages. You
          may opt out at any time.
        </p>
        <p>
          You will not be held responsible for failing to opt out of Yaruta
          messages solely because your mobile phone number changes or is
          deactivated.
        </p>
        <p>
          For information about how Yaruta handles your personal information,
          please review our{' '}
          <a
            className="underline font-medium"
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
      </Section>

      <Section title="8. Contact">
        <p>
          For support:{' '}
          <a
            className="underline font-medium"
            href="mailto:hello@yaruta.app"
          >
            hello@yaruta.app
          </a>
        </p>
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2 mt-3">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

function List({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 flex flex-col gap-1">{children}</ul>;
}
